/**
 * MyTasksPanel — Embeddable My Tasks widget
 * Visual language: matches SMS Inbox — cream background, dark charcoal rows,
 * gold/amber badges, bold uppercase section header, monospace timestamps.
 */
import { useState, useMemo, useRef, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus, CheckCircle2, Circle, Clock, AlertTriangle,
  ChevronDown, ChevronUp, Trash2, UserPlus, X, MessageSquare, Send, Pencil, Search,
} from "lucide-react";

// ─── Team members ─────────────────────────────────────────────────────────────
const FALLBACK_TEAM_MEMBERS = [
  { name: "Mr. B — Director",           email: "byjosiah@gmail.com" },
  { name: "Gus G. — Controller",         email: "gus@bbnfi.com" },
  { name: "Amy B. — Controller",         email: "amybyfield@gmail.com" },
  { name: "Jasmin — Tax Division",       email: "jasminsilva91@gmail.com" },
  { name: "Jany L. — Bkkpr Mgr",        email: "lopez.jany@gmail.com" },
  { name: "Irit P. — Bkkpr Supervisor",  email: "iritna@gmail.com" },
  { name: "Yunuen T. — Bkkpr",          email: "yunuenalfaro7@gmail.com" },
  { name: "Shobhit A. — Bkkpr",         email: "shobhit@advancefinserv.com" },
  { name: "Lulu M. — Bkkpr",            email: "lourdesmastache20@gmail.com" },
  { name: "Marian A. — Bkkpr",          email: "marianjoyhernandez2@gmail.com" },
  { name: "Renata L. — Bkkpr",          email: "erenatalazo@gmail.com" },
  { name: "Arvind K. — Bkkpr",          email: "arvind@advancefinserv.com" },
  { name: "Mirian H. — Bkkpr",          email: "mirian@apxhqhub.com" },
  { name: "Grecia S. — Asst Controller",  email: "grecia.ssoto13@gmail.com" },
  { name: "Anabel D. — Asst Controller",  email: "anabelldiazcoronado@msn.com" },
  { name: "Diana G. — Tax Division",      email: "team@mfisinc.com" },
  { name: "Luna J. — Tax Division",       email: "htminfo@gmail.com" },
];

type TeamMember = { name: string; email: string };

type Priority = "low" | "medium" | "high" | "urgent";
type Status = "todo" | "in_progress" | "done";

interface Participant {
  userEmail: string;
  userName: string;
  role: "participant" | "inform";
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatTs(ts: number | Date | null | undefined): string {
  if (!ts) return "";
  return new Date(ts).toLocaleString(undefined, {
    month: "numeric", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

const PRIORITY_BADGE: Record<Priority, { label: string; bg: string; color: string }> = {
  low:    { label: "LOW",    bg: "oklch(0.90 0.008 240)",    color: "oklch(0.30 0.04 240)" },
  medium: { label: "MEDIUM", bg: "oklch(0.82 0.07 75 / 20%)", color: "oklch(0.38 0.14 75)" },
  high:   { label: "HIGH",   bg: "oklch(0.72 0.18 55 / 20%)", color: "oklch(0.42 0.18 55)" },
  urgent: { label: "URGENT", bg: "oklch(0.72 0.18 25 / 20%)", color: "oklch(0.42 0.18 25)" },
};

const STATUS_BADGE: Record<Status, { label: string; bg: string; color: string; icon: React.ReactNode }> = {
  todo:        { label: "TO DO",       bg: "oklch(0.88 0.01 240)",    color: "oklch(0.28 0.04 240)", icon: <Circle className="w-3.5 h-3.5" /> },
  in_progress: { label: "IN PROGRESS", bg: "oklch(0.82 0.07 75 / 25%)", color: "oklch(0.38 0.14 75)", icon: <Clock className="w-3.5 h-3.5" /> },
  done:        { label: "DONE",        bg: "oklch(0.65 0.20 142 / 20%)", color: "oklch(0.30 0.18 142)", icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
};

// ─── Updates Thread ────────────────────────────────────────────────────────────
function UpdatesThread({ taskId, currentEmail, currentName }: {
  taskId: number; currentEmail: string; currentName: string;
}) {
  const utils = trpc.useUtils();
  const [text, setText] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);
  const { data: updates = [], isLoading } = trpc.myTasks.getUpdates.useQuery(
    { taskId }, { refetchInterval: 15000 }
  );
  const addUpdate = trpc.myTasks.addUpdate.useMutation({
    onSuccess: () => { utils.myTasks.getUpdates.invalidate({ taskId }); setText(""); },
    onError: (err) => toast.error("Failed to post update: " + err.message),
  });
  const deleteUpdate = trpc.myTasks.deleteUpdate.useMutation({
    onSuccess: () => utils.myTasks.getUpdates.invalidate({ taskId }),
    onError: () => toast.error("Failed to delete update"),
  });
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [updates.length]);

  return (
    <div style={{ marginTop: "12px", borderTop: "1px solid oklch(0.82 0.015 240)", paddingTop: "12px" }}>
      {isLoading ? (
        <p style={{ fontSize: "12px", color: "oklch(0.50 0.03 240)", fontStyle: "italic" }}>Loading updates…</p>
      ) : updates.length === 0 ? (
        <p style={{ fontSize: "12px", color: "oklch(0.50 0.03 240)", fontStyle: "italic" }}>No updates yet. Be the first to add one.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "160px", overflowY: "auto", marginBottom: "10px" }}>
          {updates.map((u: any) => (
            <div key={u.id} className="group" style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
              <div style={{ flex: 1, background: "oklch(0.93 0.008 240)", borderRadius: "8px", padding: "8px 12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
                  <span style={{ fontSize: "12px", fontWeight: 700, color: "oklch(0.18 0.04 240)" }}>{u.authorName}</span>
                  <span style={{ fontSize: "11px", color: "oklch(0.45 0.03 240)", fontFamily: "monospace" }}>{formatTs(u.createdAt)}</span>
                </div>
                <p style={{ fontSize: "13px", color: "oklch(0.25 0.04 240)", lineHeight: 1.5, margin: 0 }}>{u.content}</p>
              </div>
              {u.authorEmail === currentEmail.toLowerCase() && (
                <button
                  onClick={() => deleteUpdate.mutate({ id: u.id })}
                  className="opacity-0 group-hover:opacity-100 transition-all"
                  style={{ color: "oklch(0.65 0.03 240)", marginTop: "4px" }}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      )}
      <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && text.trim()) {
              e.preventDefault();
              addUpdate.mutate({ taskId, content: text.trim(), authorEmail: currentEmail, authorName: currentName });
            }
          }}
          placeholder="Add an update… (Enter to send)"
          style={{
            flex: 1, fontSize: "13px", fontWeight: 500,
            padding: "8px 12px", borderRadius: "8px", outline: "none",
            background: "white", border: "1.5px solid oklch(0.78 0.02 240)",
            color: "oklch(0.10 0.04 240)",
          }}
        />
        <button
          onClick={() => {
            if (text.trim()) addUpdate.mutate({ taskId, content: text.trim(), authorEmail: currentEmail, authorName: currentName });
          }}
          disabled={!text.trim() || addUpdate.isPending}
          style={{
            padding: "8px 12px", borderRadius: "8px", border: "none",
            background: "oklch(0.82 0.07 75)", color: "oklch(0.08 0.03 240)",
            cursor: "pointer", opacity: (!text.trim() || addUpdate.isPending) ? 0.4 : 1,
          }}
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ─── Task Card ─────────────────────────────────────────────────────────────────
function TaskCard({ task, currentEmail, currentName, teamMembers, onStatusChange, onDelete }: {
  task: any;
  currentEmail: string;
  currentName: string;
  teamMembers: TeamMember[];
  onStatusChange: (id: number, status: Status) => void;
  onDelete: (id: number) => void;
}) {
  const utils = trpc.useUtils();
  const { user: authUser } = useAuth();
  const liveEmail = (authUser?.email ?? currentEmail ?? "").toLowerCase().trim();
  const liveName = (authUser?.name ?? currentName ?? "").toLowerCase().trim();
  const [expanded, setExpanded] = useState(false);
  const [showUpdates, setShowUpdates] = useState(false);
  const [showAddTeammate, setShowAddTeammate] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addRole, setAddRole] = useState<"participant" | "inform">("participant");
  const [showEdit, setShowEdit] = useState(false);
  const [editTitle, setEditTitle] = useState(task.title ?? "");
  const [editDescription, setEditDescription] = useState(task.description ?? "");
  const [editPriority, setEditPriority] = useState<Priority>(task.priority ?? "medium");
  const [editDueAt, setEditDueAt] = useState(
    task.dueAt ? new Date(task.dueAt).toISOString().split("T")[0] : ""
  );

  const updateTaskMutation = trpc.myTasks.update.useMutation({
    onSuccess: () => { utils.myTasks.list.invalidate(); toast.success("Task updated"); setShowEdit(false); },
    onError: (err) => toast.error("Failed to update: " + err.message),
  });

  const storedEmail = (task.createdBy ?? "").toLowerCase().trim();
  const storedName = (task.createdByName ?? "").toLowerCase().trim();
  const isOwner = (storedEmail !== "" && storedEmail === liveEmail) || (storedName !== "" && storedName === liveName);

  const addParticipantMutation = trpc.myTasks.addParticipant.useMutation({
    onSuccess: () => { utils.myTasks.list.invalidate(); toast.success("Teammate added"); setAddEmail(""); setAddRole("participant"); setShowAddTeammate(false); },
    onError: (err) => toast.error("Failed to add teammate: " + err.message),
  });
  const removeParticipantMutation = trpc.myTasks.removeParticipant.useMutation({
    onSuccess: () => utils.myTasks.list.invalidate(),
    onError: () => toast.error("Failed to remove teammate"),
  });

  const existingEmails = new Set((task.participants ?? []).map((p: Participant) => p.userEmail.toLowerCase()));
  const availableToAdd = teamMembers.filter(
    m => m.email.toLowerCase() !== liveEmail && !existingEmails.has(m.email.toLowerCase())
  );

  const pBadge = PRIORITY_BADGE[task.priority as Priority] ?? PRIORITY_BADGE.medium;
  const sBadge = STATUS_BADGE[task.status as Status] ?? STATUS_BADGE.todo;
  const isDone = task.status === "done";
  const isOverdue = task.dueAt && new Date(task.dueAt) < new Date() && !isDone;
  const nextStatus: Record<Status, Status> = { todo: "in_progress", in_progress: "done", done: "todo" };

  return (
    <div style={{
      borderRadius: "12px",
      background: isDone ? "oklch(0.93 0.008 240)" : isOverdue ? "oklch(0.97 0.03 60)" : "white",
      border: isDone
        ? "1.5px solid oklch(0.82 0.015 240)"
        : isOverdue
        ? "1.5px solid oklch(0.78 0.10 60 / 60%)"
        : "1.5px solid oklch(0.85 0.015 240)",
      boxShadow: isDone ? "none" : "0 1px 4px oklch(0.60 0.02 240 / 10%)",
      opacity: isDone ? 0.75 : 1,
      transition: "all 0.12s",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: "14px", padding: "14px 16px" }}>
        {/* Status toggle button */}
        <button
          onClick={() => onStatusChange(task.id, nextStatus[task.status as Status])}
          title={`Mark as ${nextStatus[task.status as Status]}`}
          style={{ marginTop: "2px", flexShrink: 0, background: "none", border: "none", cursor: "pointer", padding: 0 }}
        >
          <span style={{ color: isDone ? "oklch(0.50 0.18 142)" : isOverdue ? "oklch(0.48 0.14 55)" : "oklch(0.40 0.04 240)" }}>
            {sBadge.icon}
          </span>
        </button>

        {/* Main content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Title row */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "10px", marginBottom: "6px" }}>
            <p style={{
              fontSize: "15px", fontWeight: 700, lineHeight: 1.3, margin: 0,
              color: isDone ? "oklch(0.50 0.03 240)" : "oklch(0.10 0.04 240)",
              textDecoration: isDone ? "line-through" : "none",
            }}>
              {task.title}
            </p>
            {/* Priority badge */}
            <span style={{
              fontSize: "11px", fontWeight: 800, letterSpacing: "0.06em",
              padding: "3px 8px", borderRadius: "6px", flexShrink: 0,
              background: pBadge.bg, color: pBadge.color,
              display: "flex", alignItems: "center", gap: "3px",
            }}>
              {(task.priority === "high" || task.priority === "urgent") && <AlertTriangle className="w-3 h-3" />}
              {pBadge.label}
            </span>
          </div>

          {/* Status + meta row */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", marginBottom: "6px" }}>
            <span style={{
              fontSize: "11px", fontWeight: 800, letterSpacing: "0.06em",
              padding: "2px 8px", borderRadius: "6px",
              background: sBadge.bg, color: sBadge.color,
              display: "flex", alignItems: "center", gap: "4px",
            }}>
              {sBadge.icon}{sBadge.label}
            </span>
            {task.dueAt && (
              <span style={{
                fontSize: "12px", fontWeight: 600, fontFamily: "monospace",
                color: isOverdue ? "oklch(0.45 0.14 55)" : "oklch(0.38 0.04 240)",
                display: "flex", alignItems: "center", gap: "4px",
              }}>
                <Clock className="w-3.5 h-3.5" />
                {new Date(task.dueAt).toLocaleDateString()}
                {isOverdue && " ⚠ Overdue"}
              </span>
            )}
            <span style={{ fontSize: "12px", color: "oklch(0.45 0.04 240)", fontWeight: 500 }}>
              By <span style={{ fontWeight: 700, color: "oklch(0.18 0.04 240)" }}>{task.createdByName}</span>
            </span>
          </div>

          {/* Participants */}
          {task.participants && task.participants.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "8px" }}>
              {task.participants.map((p: Participant) => (
                <span key={p.userEmail} style={{
                  display: "flex", alignItems: "center", gap: "5px",
                  fontSize: "12px", fontWeight: 600,
                  padding: "3px 10px", borderRadius: "20px",
                  background: p.role === "inform" ? "oklch(0.90 0.008 240)" : "oklch(0.82 0.07 75 / 18%)",
                  color: p.role === "inform" ? "oklch(0.28 0.04 240)" : "oklch(0.35 0.14 75)",
                  border: p.role === "inform" ? "1px solid oklch(0.78 0.02 240)" : "1px solid oklch(0.82 0.07 75 / 40%)",
                }}>
                  {p.userName}{p.role === "inform" ? " (inform)" : ""}
                  {isOwner && (
                    <button
                      onClick={() => removeParticipantMutation.mutate({ taskId: task.id, userEmail: p.userEmail })}
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 0, lineHeight: 1, color: "inherit" }}
                      title="Remove teammate"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}

          {/* Action row */}
          <div style={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
            {task.description && (
              <button
                onClick={() => setExpanded(!expanded)}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", fontWeight: 600, color: "oklch(0.38 0.04 240)" }}
              >
                {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                {expanded ? "Hide details" : "Details"}
              </button>
            )}
            <button
              onClick={() => setShowUpdates(!showUpdates)}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", fontWeight: 700, color: showUpdates ? "oklch(0.35 0.14 75)" : "oklch(0.38 0.04 240)" }}
            >
              <MessageSquare className="w-3.5 h-3.5" />
              {showUpdates ? "Hide updates" : "Updates"}
            </button>
            {isOwner && (
              <button
                onClick={() => { setEditTitle(task.title ?? ""); setEditDescription(task.description ?? ""); setEditPriority(task.priority ?? "medium"); setEditDueAt(task.dueAt ? new Date(task.dueAt).toISOString().split("T")[0] : ""); setShowEdit(!showEdit); }}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", fontWeight: 700, color: showEdit ? "oklch(0.38 0.14 75)" : "oklch(0.38 0.04 240)" }}
              >
                <Pencil className="w-3.5 h-3.5" />
                {showEdit ? "Cancel Edit" : "Edit"}
              </button>
            )}
            {isOwner && availableToAdd.length > 0 && (
              <button
                onClick={() => setShowAddTeammate(!showAddTeammate)}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", fontWeight: 700, color: showAddTeammate ? "oklch(0.30 0.18 142)" : "oklch(0.38 0.04 240)" }}
              >
                <UserPlus className="w-3.5 h-3.5" />
                {showAddTeammate ? "Cancel" : "+ Add Teammate"}
              </button>
            )}
          </div>

          {/* Edit form */}
          {showEdit && (
            <div style={{ marginTop: "12px", padding: "14px", background: "oklch(0.96 0.015 75)", border: "1.5px solid oklch(0.82 0.07 75 / 40%)", borderRadius: "10px" }}>
              <p style={{ fontSize: "12px", fontWeight: 800, letterSpacing: "0.06em", color: "oklch(0.38 0.14 75)", marginBottom: "10px" }}>EDIT TASK</p>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <div>
                  <label style={{ fontSize: "11px", fontWeight: 700, color: "oklch(0.35 0.04 240)", display: "block", marginBottom: "4px" }}>TITLE</label>
                  <input value={editTitle} onChange={e => setEditTitle(e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1))}
                    style={{ width: "100%", height: "36px", borderRadius: "8px", border: "1.5px solid oklch(0.82 0.07 75 / 50%)", background: "white", padding: "0 12px", fontSize: "14px", fontWeight: 600, color: "oklch(0.10 0.04 240)", outline: "none", boxSizing: "border-box" }}
                    placeholder="Task title" />
                </div>
                <div>
                  <label style={{ fontSize: "11px", fontWeight: 700, color: "oklch(0.35 0.04 240)", display: "block", marginBottom: "4px" }}>DESCRIPTION</label>
                  <textarea value={editDescription} onChange={e => setEditDescription(e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1))} rows={2}
                    style={{ width: "100%", borderRadius: "8px", border: "1.5px solid oklch(0.82 0.07 75 / 50%)", background: "white", padding: "8px 12px", fontSize: "13px", fontWeight: 500, color: "oklch(0.10 0.04 240)", outline: "none", resize: "none", boxSizing: "border-box" }}
                    placeholder="Optional details…" />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                  <div>
                    <label style={{ fontSize: "11px", fontWeight: 700, color: "oklch(0.35 0.04 240)", display: "block", marginBottom: "4px" }}>PRIORITY</label>
                    <select value={editPriority} onChange={e => setEditPriority(e.target.value as Priority)}
                      style={{ width: "100%", height: "36px", borderRadius: "8px", border: "1.5px solid oklch(0.82 0.07 75 / 50%)", background: "white", padding: "0 10px", fontSize: "13px", fontWeight: 600, color: "oklch(0.10 0.04 240)", outline: "none" }}>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: "11px", fontWeight: 700, color: "oklch(0.35 0.04 240)", display: "block", marginBottom: "4px" }}>DUE DATE</label>
                    <input type="date" value={editDueAt} onChange={e => setEditDueAt(e.target.value)}
                      style={{ width: "100%", height: "36px", borderRadius: "8px", border: "1.5px solid oklch(0.82 0.07 75 / 50%)", background: "white", padding: "0 10px", fontSize: "13px", fontWeight: 600, color: "oklch(0.10 0.04 240)", outline: "none" }} />
                  </div>
                </div>
                <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                  <button onClick={() => setShowEdit(false)}
                    style={{ height: "34px", padding: "0 16px", borderRadius: "8px", border: "1.5px solid oklch(0.78 0.02 240)", background: "white", fontSize: "12px", fontWeight: 700, color: "oklch(0.28 0.04 240)", cursor: "pointer" }}>
                    Cancel
                  </button>
                  <button
                    onClick={() => { if (!editTitle.trim()) { toast.error("Title is required"); return; } updateTaskMutation.mutate({ id: task.id, title: editTitle.trim(), description: editDescription.trim() || undefined, priority: editPriority, dueAt: editDueAt ? new Date(editDueAt) : null }); }}
                    disabled={updateTaskMutation.isPending}
                    style={{ height: "34px", padding: "0 16px", borderRadius: "8px", border: "none", background: "oklch(0.82 0.07 75)", fontSize: "12px", fontWeight: 800, color: "oklch(0.08 0.03 240)", cursor: "pointer", opacity: updateTaskMutation.isPending ? 0.5 : 1 }}>
                    {updateTaskMutation.isPending ? "Saving…" : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add Teammate form */}
          {showAddTeammate && (
            <div style={{ marginTop: "12px", padding: "12px", background: "oklch(0.95 0.015 142 / 30%)", border: "1.5px solid oklch(0.65 0.20 142 / 25%)", borderRadius: "10px", display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
              <select value={addEmail} onChange={e => setAddEmail(e.target.value)}
                style={{ flex: 1, minWidth: "160px", height: "34px", borderRadius: "8px", border: "1.5px solid oklch(0.65 0.20 142 / 40%)", background: "white", padding: "0 10px", fontSize: "13px", fontWeight: 600, color: "oklch(0.10 0.04 240)", outline: "none" }}>
                <option value="">Select teammate…</option>
                {availableToAdd.map(m => <option key={m.email} value={m.email}>{m.name}</option>)}
              </select>
              <select value={addRole} onChange={e => setAddRole(e.target.value as "participant" | "inform")}
                style={{ width: "140px", height: "34px", borderRadius: "8px", border: "1.5px solid oklch(0.65 0.20 142 / 40%)", background: "white", padding: "0 10px", fontSize: "13px", fontWeight: 600, color: "oklch(0.10 0.04 240)", outline: "none" }}>
                <option value="participant">Participant</option>
                <option value="inform">Inform only</option>
              </select>
              <button
                onClick={() => { if (!addEmail) return; const member = teamMembers.find(m => m.email === addEmail); if (!member) return; addParticipantMutation.mutate({ taskId: task.id, userEmail: member.email, userName: member.name, role: addRole, callerName: currentName }); }}
                disabled={!addEmail || addParticipantMutation.isPending}
                style={{ height: "34px", padding: "0 16px", borderRadius: "8px", border: "none", background: "oklch(0.65 0.20 142)", color: "white", fontSize: "12px", fontWeight: 800, cursor: "pointer", opacity: (!addEmail || addParticipantMutation.isPending) ? 0.4 : 1 }}>
                {addParticipantMutation.isPending ? "Adding…" : "Add"}
              </button>
            </div>
          )}

          {/* Description */}
          {expanded && task.description && (
            <p style={{ marginTop: "10px", fontSize: "13px", color: "oklch(0.28 0.04 240)", background: "oklch(0.93 0.008 240)", borderRadius: "8px", padding: "10px 14px", lineHeight: 1.6 }}>{task.description}</p>
          )}

          {/* Updates thread */}
          {showUpdates && (
            <UpdatesThread taskId={task.id} currentEmail={liveEmail} currentName={liveName || currentName} />
          )}
        </div>

        {/* Delete button */}
        {isOwner && (
          <button
            onClick={() => onDelete(task.id)}
            style={{ flexShrink: 0, background: "none", border: "none", cursor: "pointer", color: "oklch(0.72 0.02 240)", marginTop: "2px" }}
            title="Delete task"
            className="hover:text-red-500 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Create Task Dialog ────────────────────────────────────────────────────────
function CreateTaskDialog({ open, onClose, currentEmail, currentName, teamMembers }: {
  open: boolean; onClose: () => void; currentEmail: string; currentName: string; teamMembers: TeamMember[];
}) {
  const utils = trpc.useUtils();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<Priority>("medium");
  const [dueAt, setDueAt] = useState("");
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [memberSearch, setMemberSearch] = useState("");

  const createTask = trpc.myTasks.create.useMutation({
    onSuccess: () => { utils.myTasks.list.invalidate(); toast.success("Task created"); setTitle(""); setDescription(""); setPriority("medium"); setDueAt(""); setParticipants([]); onClose(); },
    onError: (err) => toast.error("Failed to create task: " + err.message),
  });

  const filteredMembers = teamMembers.filter(
    (m) => m.email !== currentEmail && !participants.find((p) => p.userEmail === m.email) &&
      (m.name.toLowerCase().includes(memberSearch.toLowerCase()) || m.email.toLowerCase().includes(memberSearch.toLowerCase()))
  );

  const addParticipant = (member: TeamMember, role: "participant" | "inform") => {
    setParticipants((prev) => [...prev, { userEmail: member.email, userName: member.name, role }]);
    setMemberSearch("");
  };

  const handleSubmit = () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    createTask.mutate({ title: title.trim(), description: description.trim() || undefined, priority, dueAt: dueAt ? new Date(dueAt) : undefined, callerEmail: currentEmail, callerName: currentName, participants: participants.length > 0 ? participants : undefined });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New Task</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Title *</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1))} placeholder="What needs to be done?" autoFocus />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Description</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1))} placeholder="Optional details…" rows={3} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Priority</label>
              <Select value={priority} onValueChange={(v) => setPriority(v as Priority)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Due Date</label>
              <Input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              <UserPlus className="w-3.5 h-3.5 inline mr-1" />Add Teammates
            </label>
            <Input value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} placeholder="Search by name or email…" />
            {memberSearch && filteredMembers.length > 0 && (
              <div className="mt-1 border rounded-lg bg-white shadow-sm max-h-36 overflow-y-auto">
                {filteredMembers.map((m) => (
                  <div key={m.email} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 text-sm">
                    <span className="text-gray-700">{m.name}</span>
                    <div className="flex gap-1">
                      <button onClick={() => addParticipant(m, "participant")} className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700 hover:bg-blue-200">Participant</button>
                      <button onClick={() => addParticipant(m, "inform")} className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 hover:bg-gray-200">Inform only</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {participants.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {participants.map((p) => (
                  <span key={p.userEmail} className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${p.role === "inform" ? "bg-gray-100 text-gray-600" : "bg-blue-50 text-blue-700"}`}>
                    {p.userName} {p.role === "inform" ? "(inform)" : ""}
                    <button onClick={() => setParticipants((prev) => prev.filter((x) => x.userEmail !== p.userEmail))}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={createTask.isPending}>{createTask.isPending ? "Creating…" : "Create Task"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Panel ────────────────────────────────────────────────────────────────
export default function MyTasksPanel() {
  const { user } = useAuth();
  const utils = trpc.useUtils();
  const [showCreate, setShowCreate] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [search, setSearch] = useState("");
  const { data: activeStaff = [] } = trpc.team.list.useQuery();
  const teamMembers: TeamMember[] = activeStaff.length > 0
    ? activeStaff.map((member: { name: string; email: string }) => ({ name: member.name, email: member.email }))
    : FALLBACK_TEAM_MEMBERS;
  const { data: tasks = [], isLoading } = trpc.myTasks.list.useQuery(
    { userEmail: user?.email ?? "", isAdmin: false },
    { enabled: !!user?.email, refetchInterval: 10000 }
  );
  const updateStatus = trpc.myTasks.updateStatus.useMutation({
    onSuccess: () => utils.myTasks.list.invalidate(),
    onError: (err) => toast.error("Failed to update status: " + err.message),
  });
  const deleteTask = trpc.myTasks.delete.useMutation({
    onSuccess: () => { utils.myTasks.list.invalidate(); toast.success("Task deleted"); },
    onError: (err) => toast.error("Failed to delete task: " + err.message),
  });

  const activeTasks = useMemo(() =>
    tasks.filter((t: any) => t.status !== "done" &&
      (!search || t.title?.toLowerCase().includes(search.toLowerCase()) || t.description?.toLowerCase().includes(search.toLowerCase()))
    ), [tasks, search]);
  const doneTasks = useMemo(() =>
    tasks.filter((t: any) => t.status === "done" &&
      (!search || t.title?.toLowerCase().includes(search.toLowerCase()))
    ), [tasks, search]);

  const counts = useMemo(() => ({
    todo: tasks.filter((t: any) => t.status === "todo").length,
    in_progress: tasks.filter((t: any) => t.status === "in_progress").length,
    done: tasks.filter((t: any) => t.status === "done").length,
  }), [tasks]);

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px", flexWrap: "wrap", gap: "10px" }}>
        <div>
          <p style={{ fontSize: "11px", fontWeight: 800, letterSpacing: "0.10em", color: "oklch(0.45 0.06 240)", textTransform: "uppercase", marginBottom: "3px" }}>
            Personal Tasks
          </p>
          <h2 style={{ fontSize: "22px", fontWeight: 900, color: "oklch(0.08 0.04 240)", fontFamily: "'Playfair Display', serif", letterSpacing: "-0.01em", margin: 0 }}>
            MY TASKS
          </h2>
          <p style={{ fontSize: "12px", color: "oklch(0.45 0.03 240)", marginTop: "2px" }}>
            Private — visible only to you and tagged teammates
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          style={{
            display: "flex", alignItems: "center", gap: "6px",
            padding: "9px 18px", borderRadius: "9px", border: "none",
            background: "oklch(0.82 0.07 75)", color: "oklch(0.08 0.03 240)",
            fontSize: "13px", fontWeight: 800, cursor: "pointer",
            boxShadow: "0 2px 8px oklch(0.82 0.07 75 / 40%)",
          }}
        >
          <Plus className="w-4 h-4" />
          New Task
        </button>
      </div>

      {/* ── Stats bar ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", marginBottom: "14px" }}>
        {[
          { key: "todo",        label: "TO DO",       countColor: "oklch(0.18 0.04 240)", bg: "oklch(0.93 0.008 240)", border: "oklch(0.82 0.015 240)" },
          { key: "in_progress", label: "IN PROGRESS", countColor: "oklch(0.38 0.14 75)",  bg: "oklch(0.96 0.015 75)",  border: "oklch(0.82 0.07 75 / 40%)" },
          { key: "done",        label: "DONE",        countColor: "oklch(0.30 0.18 142)", bg: "oklch(0.95 0.015 142 / 40%)", border: "oklch(0.65 0.20 142 / 30%)" },
        ].map((s) => (
          <div key={s.key} style={{ borderRadius: "10px", padding: "12px 14px", background: s.bg, border: `1.5px solid ${s.border}` }}>
            <p style={{ fontSize: "24px", fontWeight: 900, color: s.countColor, margin: 0, lineHeight: 1 }}>{counts[s.key as keyof typeof counts]}</p>
            <p style={{ fontSize: "10px", fontWeight: 800, letterSpacing: "0.08em", color: s.countColor, margin: "4px 0 0", opacity: 0.8 }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* ── Search ── */}
      <div style={{ position: "relative", marginBottom: "12px" }}>
        <Search size={14} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "oklch(0.45 0.04 240)", pointerEvents: "none" }} />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search tasks…"
          style={{
            width: "100%", paddingLeft: "34px", paddingRight: "12px",
            paddingTop: "9px", paddingBottom: "9px",
            borderRadius: "9px", fontSize: "13px", fontWeight: 500, outline: "none",
            background: "white", border: "1.5px solid oklch(0.82 0.015 240)",
            color: "oklch(0.10 0.04 240)", boxSizing: "border-box",
            boxShadow: "0 1px 3px oklch(0.60 0.02 240 / 8%)",
          }}
        />
      </div>

      {/* ── Active tasks ── */}
      {isLoading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {[1, 2, 3].map((i) => <div key={i} style={{ height: "72px", borderRadius: "10px", background: "oklch(0.90 0.008 240)", animation: "pulse 1.5s ease-in-out infinite" }} />)}
        </div>
      ) : activeTasks.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "oklch(0.50 0.03 240)" }}>
          <CheckCircle2 style={{ width: "36px", height: "36px", margin: "0 auto 10px", opacity: 0.3 }} />
          <p style={{ fontSize: "14px", fontWeight: 600 }}>
            {search ? "No tasks match your search" : "No active tasks"}
          </p>
          <p style={{ fontSize: "12px", marginTop: "4px", opacity: 0.7 }}>Click "New Task" to get started</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {activeTasks.map((task: any) => (
            <TaskCard
              key={task.id}
              task={task}
              currentEmail={user?.email ?? ""}
              currentName={user?.name ?? ""}
              teamMembers={teamMembers}
              onStatusChange={(id, status) => updateStatus.mutate({ id, status, callerEmail: user?.email ?? "", callerName: user?.name ?? "" })}
              onDelete={(id) => deleteTask.mutate({ id })}
            />
          ))}
        </div>
      )}

      {/* ── Completed tasks (collapsible) ── */}
      {doneTasks.length > 0 && (
        <div style={{ marginTop: "16px" }}>
          <button
            onClick={() => setShowDone(!showDone)}
            style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", cursor: "pointer", padding: "0 0 8px", fontSize: "13px", fontWeight: 700, color: "oklch(0.38 0.04 240)" }}
          >
            {showDone ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Completed ({doneTasks.length})
          </button>
          {showDone && (
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {doneTasks.map((task: any) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  currentEmail={user?.email ?? ""}
                  currentName={user?.name ?? ""}
                  teamMembers={teamMembers}
                  onStatusChange={(id, status) => updateStatus.mutate({ id, status, callerEmail: user?.email ?? "", callerName: user?.name ?? "" })}
                  onDelete={(id) => deleteTask.mutate({ id })}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <CreateTaskDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        currentEmail={user?.email ?? ""}
        currentName={user?.name ?? ""}
        teamMembers={teamMembers}
      />
    </div>
  );
}
